/* eslint-disable */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Divider, Paper, Typography, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getUserDetails } from '../../utils';
import {
  MESSAGES_PER_PAGE,
  MAX_FILE_SIZE,
  uploadChatFile,
  downloadFileWithProgress,
} from './chatUtils';
import { ROLES } from '../../constants';
import UserListPanel from './UserListPanel';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import MessagesList from './MessagesList';
import EmptyChatState from './EmptyChatState';
import NoPairsState from './NoPairsState';

const PairedUsersMessaging = ({
  pairedUsersList,
  isLoading,
  pageTitle,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  onlyPairedAndAdmin = false,
  showScheduleMeeting = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const socketInstance = useOutletContext();
  const userDetails = getUserDetails();

  const [selectedUser, setSelectedUser] = useState(null);
  const [messagesArray, setMessagesArray] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChatPanel, setShowChatPanel] = useState(false);
  const [lastMessages, setLastMessages] = useState({});
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [pendingFile, setPendingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [fileError, setFileError] = useState('');

  const [unreadCountByUserId, setUnreadCountByUserId] = useState({});
  const [typingUserId, setTypingUserId] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());

  const [chatPage, setChatPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [sortedPairedUsers, setSortedPairedUsers] = useState([]);
  const [conversationPreviewsFetched, setConversationPreviewsFetched] = useState(false);

  const roomIdRef = useRef('');
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const topSentinelRef = useRef(null);
  const inputRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const openChatAtRef = useRef(null);
  const preventScrollRef = useRef(false);
  const prevScrollHeightRef = useRef(0);

  useEffect(() => {
    if (pairedUsersList?.length) {
      setSortedPairedUsers((prev) => {
        if (prev.length) return prev;
        return pairedUsersList;
      });
    }
  }, [pairedUsersList]);

  const filteredUsers = React.useMemo(() => {
    const list = sortedPairedUsers.length ? sortedPairedUsers : pairedUsersList ?? [];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter((user) => {
      const fullName = `${user?.fname || ''} ${user?.lname || ''}`.toLowerCase();
      return fullName.includes(q) || user?.email?.toLowerCase().includes(q);
    });
  }, [sortedPairedUsers, pairedUsersList, searchQuery]);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    try {
      messagesEndRef.current?.scrollIntoView({ behavior });
    } catch (e) {}
  }, []);

  const restoreScrollPosition = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const diff = container.scrollHeight - prevScrollHeightRef.current;
    container.scrollTop = diff;
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    const list = sortedPairedUsers.length ? sortedPairedUsers : pairedUsersList ?? [];
    if (!list.length) return;
    const ids = list.map((u) => u._id?.toString?.() ?? u._id);
    socket.emit('getOnlineStatus', { userIds: ids }, (statusMap) => {
      if (!statusMap) return;
      const onlineSet = new Set(Object.keys(statusMap).filter((id) => statusMap[id]));
      setOnlineUsers(onlineSet);
    });
    const handleUserOnline = ({ userId }) => {
      if (ids.includes(userId)) setOnlineUsers((prev) => new Set([...prev, userId]));
    };
    const handleUserOffline = ({ userId }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    };
    socket.on('userOnline', handleUserOnline);
    socket.on('userOffline', handleUserOffline);
    return () => {
      socket.off('userOnline', handleUserOnline);
      socket.off('userOffline', handleUserOffline);
    };
  }, [socketInstance[8], pairedUsersList, sortedPairedUsers]);

  const applyConversationPreviews = useCallback(
    (previews) => {
      if (!Array.isArray(previews)) return;
      const unreadNext = {};
      const lastMsgNext = {};
      previews.forEach(({ otherUserId, otherUser, lastMessage, unreadCount }) => {
        if (unreadCount > 0) unreadNext[otherUserId] = unreadCount;
        if (lastMessage) lastMsgNext[otherUserId] = lastMessage;
      });
      setUnreadCountByUserId((prev) => ({ ...prev, ...unreadNext }));
      setLastMessages((prev) => ({ ...prev, ...lastMsgNext }));
      const pairedMap = new Map((pairedUsersList || []).map((u) => [u._id?.toString?.() ?? u._id, u]));
      const allowedPreviews = onlyPairedAndAdmin
        ? previews.filter(
            (p) => pairedMap.has(p.otherUserId) || p.otherUser?.role === ROLES.ADMIN
          )
        : previews;
      const ordered = allowedPreviews
        .map((p) => {
          const user = p.otherUser || pairedMap.get(p.otherUserId);
          if (!user) return null;
          return typeof user.toObject === 'function'
            ? { ...user.toObject(), _id: user._id }
            : { ...user, _id: user._id };
        })
        .filter(Boolean);
      const orderedIds = new Set(ordered.map((u) => u._id?.toString?.() ?? u._id));
      (pairedUsersList || []).forEach((u) => {
        const id = u._id?.toString?.() ?? u._id;
        if (!orderedIds.has(id)) {
          ordered.push(u);
          orderedIds.add(id);
        }
      });
      setSortedPairedUsers(ordered);
    },
    [pairedUsersList, onlyPairedAndAdmin]
  );

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    socket.emit('getConversationPreviews', (previews) => {
      setConversationPreviewsFetched(true);
      applyConversationPreviews(previews);
    });
  }, [socketInstance[8], pairedUsersList, applyConversationPreviews]);

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    const handler = () => {
      socket.emit('getConversationPreviews', (previews) => {
        applyConversationPreviews(previews);
      });
    };
    socket.on('conversationListUpdated', handler);
    return () => socket.off('conversationListUpdated', handler);
  }, [socketInstance[8], applyConversationPreviews]);

  useEffect(() => {
    if (!selectedUser || !socketInstance[8]) return;
    setLoadingMessages(true);
    setMessagesArray([]);
    setChatPage(1);
    setHasMoreMessages(false);
    roomIdRef.current = '';
    socketInstance[8].emit('joinRoom', { receiverId: selectedUser._id }, (newRoomId) => {
      roomIdRef.current = newRoomId;
      setRoomId(newRoomId);
    });
  }, [selectedUser, socketInstance[8]]);

  useEffect(() => {
    if (!roomId || !socketInstance[8]) return;
    socketInstance[8].emit(
      'roomMessages',
      { roomId, page: 1, limit: MESSAGES_PER_PAGE },
      (result) => {
        const msgs = result?.messages ?? result ?? [];
        const pagination = result?.pagination;
        setMessagesArray(msgs);
        setHasMoreMessages(pagination?.hasMore ?? false);
        setChatPage(1);
        setLoadingMessages(false);
        socketInstance[8].emit('markChatRead', { roomId }, () => {
          if (selectedUser?._id)
            setUnreadCountByUserId((prev) => ({ ...prev, [selectedUser._id]: 0 }));
        });
        openChatAtRef.current = Date.now();
        if (msgs.length > 0 && selectedUser) {
          setLastMessages((prev) => ({ ...prev, [selectedUser._id]: msgs[msgs.length - 1] }));
        }
        preventScrollRef.current = false;
        setTimeout(() => scrollToBottom('auto'), 50);
      }
    );
  }, [roomId]);

  useEffect(() => {
    if (!socketInstance[8]) return;
    const handler = (incoming) => {
      const payload =
        incoming && typeof incoming === 'object' && !Array.isArray(incoming)
          ? incoming
          : { messages: incoming };
      const msgs = payload?.messages ?? (Array.isArray(incoming) ? incoming : []);
      const msgRoomId = payload?.roomId;
      if (!msgRoomId || msgRoomId === roomIdRef.current) {
        setMessagesArray(msgs);
        preventScrollRef.current = false;
      }
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg) {
        const senderId = lastMsg?.senderId?._id || lastMsg?.senderId?.id;
        const otherUserId =
          senderId === userDetails?.id ? selectedUser?._id : senderId;
        if (otherUserId) {
          setLastMessages((prev) => ({ ...prev, [otherUserId]: lastMsg }));
          setSortedPairedUsers((prev) => {
            const idStr = otherUserId?.toString?.() ?? otherUserId;
            const idx = prev.findIndex((u) => (u._id?.toString?.() ?? u._id) === idStr);
            if (idx === 0) return prev;
            const next = [...prev];
            if (idx > 0) {
              const [user] = next.splice(idx, 1);
              next.unshift(user);
              return next;
            }
            const sender = lastMsg.senderId;
            const userObj =
              sender && (sender._id || sender.id)
                ? {
                    _id: sender._id || sender.id,
                    fname: sender.fname,
                    lname: sender.lname,
                    image: sender.image,
                    email: sender.email,
                  }
                : null;
            if (userObj && (!onlyPairedAndAdmin || sender?.role === ROLES.ADMIN))
              next.unshift(userObj);
            return next;
          });
        }
        if (
          msgRoomId &&
          msgRoomId !== roomIdRef.current &&
          lastMsg.senderId?._id !== userDetails?.id
        ) {
          const otherId = lastMsg.senderId?._id;
          if (otherId)
            setUnreadCountByUserId((prev) => ({
              ...prev,
              [otherId]: (prev[otherId] || 0) + 1,
            }));
        }
      }
    };
    socketInstance[8].on('newMessage', handler);
    return () => socketInstance[8].off('newMessage', handler);
  }, [socketInstance[8], selectedUser, userDetails?.id]);

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    const handler = ({ messageId, reactions }) => {
      setMessagesArray((prev) =>
        prev.map((msg) => (msg._id === messageId ? { ...msg, reactions } : msg))
      );
    };
    socket.on('reactionUpdated', handler);
    return () => socket.off('reactionUpdated', handler);
  }, [socketInstance[8]]);

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    const handler = ({ roomId }) => {
      if (roomId !== roomIdRef.current) return;
      setMessagesArray((prev) =>
        prev.map((msg) =>
          msg?.senderId?._id === userDetails?.id && !msg.isSeen
            ? { ...msg, isSeen: true }
            : msg
        )
      );
    };
    socket.on('chatRead', handler);
    return () => socket.off('chatRead', handler);
  }, [socketInstance[8], userDetails?.id]);

  useEffect(() => {
    const socket = socketInstance[8];
    if (!socket) return;
    const handler = ({ userId, roomId, typing }) => {
      if (roomId !== roomIdRef.current) return;
      if (userId === userDetails?.id) return;
      setTypingUserId(typing ? userId : null);
    };
    socket.on('userTyping', handler);
    return () => {
      socket.off('userTyping', handler);
      setTypingUserId(null);
    };
  }, [socketInstance[8], userDetails?.id]);

  useEffect(() => {
    if (messagesArray.length === 0) return;
    if (preventScrollRef.current) {
      restoreScrollPosition();
      preventScrollRef.current = false;
    } else {
      scrollToBottom('smooth');
    }
  }, [messagesArray, scrollToBottom, restoreScrollPosition]);

  const loadOlderMessages = useCallback(() => {
    if (
      loadingMoreMessages ||
      !hasMoreMessages ||
      !roomIdRef.current ||
      !socketInstance[8]
    )
      return;
    setLoadingMoreMessages(true);
    const nextPage = chatPage + 1;
    prevScrollHeightRef.current = messagesContainerRef.current?.scrollHeight ?? 0;
    preventScrollRef.current = true;
    socketInstance[8].emit(
      'roomMessages',
      {
        roomId: roomIdRef.current,
        page: nextPage,
        limit: MESSAGES_PER_PAGE,
      },
      (result) => {
        const olderMsgs = result?.messages ?? result ?? [];
        const pagination = result?.pagination;
        setMessagesArray((prev) => [...olderMsgs, ...prev]);
        setHasMoreMessages(pagination?.hasMore ?? false);
        setChatPage(nextPage);
        setLoadingMoreMessages(false);
      }
    );
  }, [chatPage, hasMoreMessages, loadingMoreMessages, socketInstance]);

  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMoreMessages &&
          !loadingMoreMessages &&
          roomIdRef.current
        ) {
          loadOlderMessages();
        }
      },
      { root: messagesContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreMessages, loadingMoreMessages, loadOlderMessages]);

  const handleFileButtonClick = () => {
    if (!roomIdRef.current) return;
    setFileError('');
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size exceeds the 10 MB limit. Please choose a smaller file.');
      e.target.value = '';
      return;
    }
    setFileError('');
    setPendingFile(file);
    e.target.value = '';
  };

  const handleRemovePendingFile = () => {
    setPendingFile(null);
    setUploadProgress(null);
    setFileError('');
  };

  const handleDownload = useCallback((attachment, messageId) => {
    if (!attachment?.s3Key || !attachment?.fileName) return;
    downloadFileWithProgress(
      attachment.s3Key,
      attachment.fileName,
      messageId,
      setDownloadProgress
    );
  }, []);

  const handleAddReaction = useCallback((messageId, emoji) => {
    if (!messageId || !emoji || !socketInstance[8]) return;
    socketInstance[8].emit('addReaction', { messageId, emoji });
  }, [socketInstance]);

  const handleSelectUser = (user) => {
    if (selectedUser?._id === user._id) return;
    setTypingUserId(null);
    setPendingFile(null);
    setUploadProgress(null);
    setFileError('');
    setSelectedUser(user);
    setUserMessage('');
    setShowEmojiPicker(false);
    if (isMobile) setShowChatPanel(true);
  };

  const containsOnlySpaces = () => /^\s*$/.test(userMessage);

  const handleSendMessage = async () => {
    const hasText = userMessage && !containsOnlySpaces();
    const hasFile = !!pendingFile;
    if (!roomIdRef.current) return;
    if (!hasText && !hasFile) return;
    socketInstance[8]?.emit('typingStop', { roomId: roomIdRef.current });
    setShowEmojiPicker(false);
    if (hasFile) {
      setUploadProgress(0);
      try {
        const result = await uploadChatFile(
          pendingFile,
          roomIdRef.current,
          (pct) => setUploadProgress(pct)
        );
        socketInstance[8]?.emit('sendMessage', {
          roomId: roomIdRef.current,
          message: hasText ? userMessage : '',
          fileAttachment: {
            url: result.url,
            fileName: result.fileName,
            fileType: result.fileType,
            fileSize: result.fileSize,
            s3Key: result.s3Key,
          },
        });
        setPendingFile(null);
        setUploadProgress(null);
        setUserMessage('');
      } catch (err) {
        setUploadProgress(null);
        setFileError(err.message || 'File upload failed. Please try again.');
      }
    } else {
      socketInstance[8]?.emit('sendMessage', {
        roomId: roomIdRef.current,
        message: userMessage,
      });
      setUserMessage('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const insertEmoji = (emoji) => {
    const startPos = inputRef.current?.selectionStart ?? userMessage.length;
    const endPos = inputRef.current?.selectionEnd ?? userMessage.length;
    setUserMessage(
      userMessage.substring(0, startPos) + emoji + userMessage.substring(endPos)
    );
  };

  const emitTypingStart = useCallback(() => {
    if (roomIdRef.current && socketInstance[8])
      socketInstance[8].emit('typingStart', { roomId: roomIdRef.current });
  }, [socketInstance]);

  const emitTypingStop = useCallback(() => {
    if (roomIdRef.current && socketInstance[8])
      socketInstance[8].emit('typingStop', { roomId: roomIdRef.current });
  }, [socketInstance]);

  const handleInputChange = useCallback(
    (e) => {
      setUserMessage(e.target.value);
      if (!roomIdRef.current || !socketInstance[8]) return;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      emitTypingStart();
      typingTimeoutRef.current = setTimeout(() => {
        emitTypingStop();
        typingTimeoutRef.current = null;
      }, 2000);
    },
    [emitTypingStart, emitTypingStop]
  );

  const handleInputBlur = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    emitTypingStop();
  }, [emitTypingStop]);

  const handleScheduleMeeting = () => {
    if (!selectedUser) return;
    navigate(
      `/dashboard/user-timeslots?id=${selectedUser._id}&name=${selectedUser.fname}`
    );
  };

  const handleViewProfile = () => {
    if (!selectedUser) return;
    const currentUserRole = userDetails?.role?.toLowerCase();
    navigate(
      `/dashboard/user-profile?id=${selectedUser._id}&role=${currentUserRole}&pairedUsers=1`
    );
  };

  const selectedFullName = selectedUser
    ? `${selectedUser?.fname || ''} ${selectedUser?.lname || ''}`.trim()
    : '';
  const isUploadingFile = uploadProgress !== null;
  const canSend =
    !isUploadingFile &&
    (!!pendingFile || (!!userMessage && !containsOnlySpaces()));

  const userListPanel = (
    <UserListPanel
      pageTitle={pageTitle}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      isLoading={isLoading}
      filteredUsers={filteredUsers}
      selectedUser={selectedUser}
      lastMessages={lastMessages}
      userDetails={userDetails}
      onlineUsers={onlineUsers}
      unreadCountByUserId={unreadCountByUserId}
      onSelectUser={handleSelectUser}
      isMobile={isMobile}
      showChatPanel={showChatPanel}
    />
  );

  const chatPanel = (
    <Box
      sx={{
        flex: 1,
        display: {
          xs: isMobile && !showChatPanel ? 'none' : 'flex',
          md: 'flex',
        },
        flexDirection: 'column',
        bgcolor: isDark
          ? alpha(theme.palette.common.white, 0.02)
          : alpha(theme.palette.grey[500], 0.03),
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {selectedUser ? (
        <>
          <ChatHeader
            selectedUser={selectedUser}
            selectedFullName={selectedFullName}
            isMobile={isMobile}
            isDark={isDark}
            theme={theme}
            onlineUsers={onlineUsers}
            onBack={() => setShowChatPanel(false)}
            onViewProfile={handleViewProfile}
            onScheduleMeeting={handleScheduleMeeting}
            showScheduleMeeting={showScheduleMeeting && selectedUser?.role !== ROLES.ADMIN}
          />

          <MessagesList
            messagesArray={messagesArray}
            loadingMessages={loadingMessages}
            loadingMoreMessages={loadingMoreMessages}
            hasMoreMessages={hasMoreMessages}
            selectedUser={selectedUser}
            userDetails={userDetails}
            openChatAtRef={openChatAtRef}
            messagesContainerRef={messagesContainerRef}
            topSentinelRef={topSentinelRef}
            messagesEndRef={messagesEndRef}
            downloadProgress={downloadProgress}
            onDownload={handleDownload}
            onReact={handleAddReaction}
          />

          <ChatInput
            userMessage={userMessage}
            selectedUser={selectedUser}
            typingUserId={typingUserId}
            fileError={fileError}
            pendingFile={pendingFile}
            isUploadingFile={isUploadingFile}
            uploadProgress={uploadProgress}
            showEmojiPicker={showEmojiPicker}
            canSend={canSend}
            inputRef={inputRef}
            fileInputRef={fileInputRef}
            emojiPickerRef={emojiPickerRef}
            onMessageChange={handleInputChange}
            onInputBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            onSendMessage={handleSendMessage}
            onInsertEmoji={insertEmoji}
            onToggleEmojiPicker={() => setShowEmojiPicker((v) => !v)}
            onFileButtonClick={handleFileButtonClick}
            onFileSelect={handleFileSelect}
            onRemovePendingFile={handleRemovePendingFile}
            onClearFileError={() => setFileError('')}
          />
        </>
      ) : (
        (sortedPairedUsers.length > 0 || pairedUsersList?.length > 0) ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <EmptyChatState selectedUser={null} />
          </Box>
        ) : null
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          overflow: 'hidden',
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: isDark
            ? '0px 4px 24px rgba(0,0,0,0.4)'
            : '0px 4px 24px rgba(0,0,0,0.06)',
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (conversationPreviewsFetched ? sortedPairedUsers.length === 0 : !pairedUsersList?.length) ? (
          <NoPairsState
            Icon={emptyIcon}
            title={emptyTitle}
            description={emptyDescription}
          />
        ) : (
          <>
            {userListPanel}
            <Divider orientation="vertical" flexItem />
            {chatPanel}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default PairedUsersMessaging;
